import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { PrismTable } from '@mylighthouse/prism-ember';
import type { Option } from '@mylighthouse/prism-ember/types/components/prism/select-input';
import type { CustomEmberTableColumn } from '@mylighthouse/prism-ember/types/components/prism/table';
import SettingsRecommendationsPriceRangeTdDow from 'lumen/components/settings/recommendations/price-range/td-dow';
import SettingsRecommendationsPriceRangeTdStrategyCondition from 'lumen/components/settings/recommendations/price-range/td-strategy-condition';
import type { ConditionalStrategy, RecommendationStrategy } from 'lumen/models';
import type {
  OperandCondition,
  PriceRangeSettings,
} from 'lumen/models/recommendation-strategy';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import { PRICE_RANGE_STRATEGY_CONDITION_TYPES } from 'lumen/utils/constants/pricing-assistant-constants';
import { getWeekDayColumns } from 'lumen/utils/pricing-assistant/recommendation-settings';

type StrategyTransformedTableRow = {
  [key: number]: object;
  addCondition?: () => void;
  children?: StrategyTransformedTableRow[];
  deleteCondition?: () => void;
  label: string;
  onConditionOperatorChange?: (operation: Option & { id: string }) => void;
  onConditionTypeChange?: (type: Option & { id: string }) => void;
  onConditionValueChange?: (id: string, value: number | null) => void;
  setting: string;
};

interface SettingsRecommendationsPriceRangeStrategyTableSignature {
  Args: {
    isEditMode: boolean;
    isSaveStrategyTaskRunning: boolean;
    roomOffsetHierarchy?: string[];
    strategy: RecommendationStrategy;
  };
}

export default class SettingsRecommendationsPriceRangeStrategyTable extends Component<SettingsRecommendationsPriceRangeStrategyTableSignature> {
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare intl: Services['intl'];
  @service declare store: Services['store'];
  @service declare usageAnalytics: Services['usage-analytics'];

  // eslint-disable-next-line ember/no-tracked-properties-from-args
  @tracked strategy = this.args.strategy;

  get columns(): CustomEmberTableColumn[] {
    return [
      {
        isEditing: this.args.isEditMode,
        name: '',
        onDayOfWeekValueChange: this.onDayOfWeekValueChange.bind(this),
        strategy: this.strategy,
        tdClassNames: 'td-strategy-table',
        tdComponent: SettingsRecommendationsPriceRangeTdStrategyCondition,
        valuePath: 'setting',
      },
      ...getWeekDayColumns().map((column) => ({
        ...column,
        isEditing: this.args.isEditMode,
        name: this.intl.t(`days_short.${column.isoWeekDay}`),
        onDayOfWeekValueChange: this.onDayOfWeekValueChange.bind(this),
        strategy: this.strategy,
        isSaveStrategyTaskRunning: this.args.isSaveStrategyTaskRunning,
        tdClassNames: 'td-strategy-table',
        tdComponent: SettingsRecommendationsPriceRangeTdDow,
        textAlign: 'left' as const,
        valuePath: column.id.toString(),
      })),
    ];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get priceRangeSettings() {
    const { settings } = this.strategy;
    return settings.settings_type === 'price_range' ? settings : null;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get rows() {
    const { isEditMode } = this.args;
    const { conditionalStrategies, conditions } = this.strategy;

    const baseSettings = this.priceRangeSettings;
    const rows = this.transformSettingToTableRows(
      baseSettings?.day_of_week ?? {},
    );

    const conditionalStrategySettings = conditionalStrategies[0]?.settings;

    if (!conditionalStrategySettings && (!isEditMode || conditions)) {
      return rows;
    }

    // Add conditional strategy settings (only one conditional strategy is supported for now)
    const conditionRow: StrategyTransformedTableRow = {
      addCondition: this.addCondition.bind(this),
      deleteCondition: this.deleteCondition.bind(this),
      label: '',
      onConditionOperatorChange: this.onConditionOperatorChange.bind(this),
      onConditionTypeChange: this.onConditionTypeChange.bind(this),
      onConditionValueChange: this.onConditionValueChange.bind(this),
      setting: 'condition',
    };

    if (
      conditionalStrategySettings &&
      conditionalStrategySettings.settings_type === 'price_range'
    ) {
      conditionRow.children = this.transformSettingToTableRows(
        conditionalStrategySettings.day_of_week,
        true,
      );
    }

    rows.push(conditionRow);

    return rows;
  }

  // Helper function to transform the settings object into a table row format
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  transformSettingToTableRows(
    settings: PriceRangeSettings['day_of_week'],
    isConditional = false,
  ) {
    const transformed: StrategyTransformedTableRow[] = [
      {
        label: this.intl.t('general.minimum'),
        setting: 'minimum',
      },
      {
        label: this.intl.t('general.maximum'),
        setting: 'maximum',
      },
    ];

    for (const key in settings) {
      if (transformed[0] && settings[key]) {
        transformed[0][key] = {
          rawValue: settings[key]?.minimum,
          formattedValue:
            settings[key]?.minimum !== null
              ? this.hotelAndMarketService.formatRate(
                  settings[key]?.minimum ?? 0,
                )
              : this.intl.t(
                  'pricing_assistant.recommendations.price_range.no_min',
                ),
          isConditional,
        };
      }

      if (transformed[1] && settings[key]) {
        transformed[1][key] = {
          rawValue: settings[key]?.maximum,
          formattedValue:
            settings[key]?.maximum !== null
              ? this.hotelAndMarketService.formatRate(
                  settings[key]?.maximum ?? 0,
                )
              : this.intl.t(
                  'pricing_assistant.recommendations.price_range.no_max',
                ),
          isConditional,
        };
      }
    }

    return transformed;
  }

  @action addCondition(): void {
    const conditionalStrategy = this.store.createRecord<ConditionalStrategy>(
      'conditional-strategy',
      {
        conditionalStrategies: [],
        conditions: null,
        createdAt: null,
        id: null,
        // BE requires a name for conditional strategy, so we hardcode a dummy name
        name: `condition-strategy-${this.strategy.id}`,
        seasonEndDt: this.strategy.seasonEndDt,
        seasonStartDt: this.strategy.seasonStartDt,
        settings: this.strategy.settings,
        settingsType: 'price_range',
        subscriptionId: parseInt(this.hotelAndMarketService.hotel.id!, 10),
        updatedAt: null,
      },
    );

    this.strategy.conditionalStrategies.push(conditionalStrategy);

    if (this.strategy.isDefaultStrategy) {
      this.usageAnalytics.track('Default Min/Max prices Condition added', {
        eventTrigger: 'recommendation-settings',
        product: 'pricing-manager',
      });
    } else {
      this.usageAnalytics.track('Seasonal Min/Max prices Condition added', {
        eventTrigger: 'recommendation-settings',
        product: 'pricing-manager',
      });
    }
  }

  @action deleteCondition(): void {
    this.strategy.conditionalStrategies.forEach((conditionalStrategy) => {
      conditionalStrategy.conditions = null;
    });

    // @ts-expect-error: Incorrect type
    this.strategy.conditionalStrategies = [];
  }

  @action onConditionOperatorChange(operation: Option & { id: string }): void {
    if (!this.strategy.conditionalStrategies[0]) {
      return;
    }

    this.strategy.conditionalStrategies[0].conditions = {
      ...this.strategy.conditionalStrategies[0].conditions,
      operation: operation.id,
    } as OperandCondition;
  }

  @action onConditionTypeChange(type: Option & { id: string }): void {
    if (!this.strategy.conditionalStrategies[0]) {
      return;
    }

    this.strategy.conditionalStrategies[0].conditions = {
      ...this.strategy.conditionalStrategies[0].conditions,
      condition_type: 'operand',
      first: {
        condition_type: 'computed_value',
        name: type.id,
      },
      // Reset the conditional value when the condition type changes
      second: {
        condition_type: 'user_editable_value',
        value: null,
      },
    } as OperandCondition;
  }

  @action onConditionValueChange(
    conditionTypeId: string,
    value: number | null,
  ): void {
    if (!this.strategy || !this.strategy.conditionalStrategies[0]) {
      return;
    }

    let formattedValue = value;

    if (
      conditionTypeId === PRICE_RANGE_STRATEGY_CONDITION_TYPES.OTB ||
      conditionTypeId === PRICE_RANGE_STRATEGY_CONDITION_TYPES.OTB_FORECAST
    ) {
      formattedValue = value !== null ? value / 100 : null;
    }

    this.strategy.conditionalStrategies[0].conditions = {
      ...this.strategy.conditionalStrategies[0].conditions,
      second: {
        condition_type: 'user_editable_value',
        value: formattedValue,
      },
    } as OperandCondition;
  }

  @action onDayOfWeekValueChange(
    dayOfWeek: number,
    setting: 'maximum' | 'minimum',
    isConditional: boolean,
    value: number | null,
  ): void {
    const formattedValue = value;

    const baseSettings = this.strategy.settings;
    if (isConditional && this.strategy.conditionalStrategies[0]) {
      const conditionalStrategySettings =
        this.strategy.conditionalStrategies[0].settings;
      if (
        !conditionalStrategySettings ||
        conditionalStrategySettings.settings_type !== 'price_range'
      ) {
        return;
      }

      this.strategy.conditionalStrategies[0].settings = {
        ...conditionalStrategySettings,
        day_of_week: {
          ...conditionalStrategySettings.day_of_week,
          [dayOfWeek]: {
            ...(conditionalStrategySettings.day_of_week[dayOfWeek] ?? {
              maximum: null,
              minimum: null,
            }),
            [setting]: formattedValue,
          },
        },
      };
    } else {
      if (baseSettings.settings_type !== 'price_range') {
        return;
      }
      this.strategy.settings = {
        ...baseSettings,
        day_of_week: {
          ...baseSettings.day_of_week,
          [dayOfWeek]: {
            ...(baseSettings.day_of_week[dayOfWeek] ?? {
              maximum: null,
              minimum: null,
            }),
            [setting]: formattedValue,
          },
        },
      };
    }
  }

  <template>
    <PrismTable
      @columns={{this.columns}}
      @rows={{this.rows}}
      @widthConstraint="eq-container"
    />
  </template>
}
